import { HttpClient, HttpParams } from "@angular/common/http";
import { BehaviorSubject } from "rxjs";
import { AlertService } from "../alert.service";
import { Injectable } from "@angular/core";
import * as moment from "moment-timezone";
import { StorageService } from "../storage.service";
import { DashboardFilter, DashboardReportsFilter, DashboardSections, OfferIntents, PortalSessionTypes } from "../../_models";
import { ReportTypes, ReportVersion, ResolveSummaryQuery } from "../../reports/models/reports";

@Injectable({
  providedIn: "root",
})
export class DashboardService {
  protected filter: BehaviorSubject<DashboardFilter>;

  protected initializeFilter(): DashboardFilter {
    // Get timezone from localStorage or guess from browser
    let timezone = this.getTimezoneFromStorage();
    if (!timezone) {
      timezone = moment.tz.guess();
    }
    return {
      start_date: moment()
        .subtract(7, "days")
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss"),
      end_date: moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
      compare_start_date: moment()
        .subtract(14, "days")
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss"),
      compare_end_date: moment()
        .subtract(8, "days")
        .endOf("day")
        .format("YYYY-MM-DD HH:mm:ss"),
      section: DashboardSections.Overview,
      includeTest: false,
      includeHangups: false,
      includeUnknowns: false,
      portal_session_types: [...PortalSessionTypes],
      timezone: timezone,
    };
  }

  protected getTimezoneFromStorage(): string {
    const itemStr = localStorage.getItem('timezone');
    if (!itemStr) {
      return null;
    }
    try {
      const item = JSON.parse(itemStr);
      const now = new Date();
      // Check if item has expiry (StorageService format)
      if (item && item.expiry) {
        if (now.getTime() > item.expiry) {
          localStorage.removeItem('timezone');
          return null;
        }
        return item.value;
      }
      // If it's just a plain string value (legacy format)
      return item;
    } catch (e) {
      // If parsing fails, it might be a plain string
      return itemStr;
    }
  }

  protected data: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  protected route: string = "";
  protected reportType: number = null;
  protected group: string = null;

  constructor(
    protected http: HttpClient,
    protected alertService: AlertService,
    protected storageService: StorageService
  ) {
    this.filter = new BehaviorSubject<DashboardFilter>(this.initializeFilter());
  }

  getQueryParams() {
    let filters = this.filter.getValue();
    let startDate = filters.start_date;
    let endDate = filters.end_date;
    let query_params = {};

    // Convert dates to UTC based on the selected timezone
    query_params["start_date"] = this.convertDateToUtc(startDate, filters.timezone);
    query_params["end_date"] = this.convertDateToUtc(endDate, filters.timezone);

    if (filters.campaign_id) {
      query_params["campaign_id"] = filters.campaign_id;
    }

    if (filters.account_id) {
      query_params["account_id"] = filters.account_id;
    }

    query_params["include_tests"] = filters.includeTest;
    query_params["include_hangups"] = filters.includeHangups;
    query_params["include_unknowns"] = filters.includeUnknowns;
    return new HttpParams({ fromObject: query_params });
  }

  /**
   * Converts a date string to UTC based on the selected timezone.
   * Interprets the date string as being in the selected timezone, then converts to UTC.
   */
  convertDateToUtc(dateStr: string, timezone: string): string {
    return moment.tz(dateStr, "YYYY-MM-DD HH:mm:ss", timezone).utc().format("YYYY-MM-DD HH:mm:ss");
  }

  getBody(): DashboardReportsFilter {
    const filterValues = this.getFilter();
    const body: DashboardReportsFilter = {
      fields: [
        "exclude_tests",
        "exclude_hangups",
        "exclude_unknowns",
        "exclude_child_sessions",
        "exclude_child_orders",
      ],
      date_range: {
        startDate: this.convertDateToUtc(filterValues.start_date, filterValues.timezone),
        endDate: this.convertDateToUtc(filterValues.end_date, filterValues.timezone),
      },
      timezone: filterValues.timezone,
      date_filter_type: "session",
      filters: {},
    };

    if (filterValues.account_id && filterValues.account_id !== "null") {
      body.accounts = [Number(filterValues.account_id)];
    }

    if (filterValues.campaign_id) {
      body.filters = { campaign_id: [Number(filterValues.campaign_id)] };
    }

    if (filterValues.crm_id) {
      body.filters = { crm_id: Number(filterValues.crm_id) };
    }

    return body;
  }

  protected getPortalBody(): DashboardReportsFilter {
    const body = this.getBody();
    body.filters = { ...body.filters, session_type: this.getFilter().portal_session_types };
    return body;
  }

  getResolveSummaryBody(): ResolveSummaryQuery {
    const filterValues = this.getFilter();
    const body: ResolveSummaryQuery = {
      report_type: ReportTypes.RESOLVE,
      date_range: {
        startDate: this.convertDateToUtc(filterValues.start_date, filterValues.timezone),
        endDate: this.convertDateToUtc(filterValues.end_date, filterValues.timezone),
      },
      timezone: filterValues.timezone,
      accounts: [],
      filters: {},
    };

    if (filterValues.compare_start_date && filterValues.compare_end_date) {
      body.compare_date_range = this.getCompareDateRange();
    }

    if (filterValues.account_id && filterValues.account_id !== "null") {
      body.accounts = [Number(filterValues.account_id)];
    }

    if (filterValues.merchant_profile_id) {
      body.filters = { merchant_profile: [Number(filterValues.merchant_profile_id)] };
    }

    return body;
  }

  protected getCompareDateRange(): { startDate: string; endDate: string } {
    const filterValues = this.getFilter();
    return {
      startDate: this.convertDateToUtc(filterValues.compare_start_date, filterValues.timezone),
      endDate: this.convertDateToUtc(filterValues.compare_end_date, filterValues.timezone),
    };
  }

  // Used for Clickhouse (Subscription & Order) Dashboards.
  getV2Body(reportType: number, group?: string): DashboardReportsFilter {
    const filterValues = this.getFilter();
    const body: DashboardReportsFilter = {
      report_version: ReportVersion.Clickhouse,
      report_type: reportType,
      date_range: {
        startDate: this.convertDateToUtc(filterValues.start_date, filterValues.timezone),
        endDate: this.convertDateToUtc(filterValues.end_date, filterValues.timezone),
      },
      compare_date_range: this.getCompareDateRange(),
      timezone: filterValues.timezone,
      accounts: [],
      filters: {},
      date_filter_type: 'session',
      fields: ['exclude_validations'],
    };

    // Generic key: the engine remaps it to hold_/subscription_/rma_intent per report type.
    if (filterValues.offer_intent) {
      body.filters['offer_intent'] = Number(filterValues.offer_intent);
    }

    if (filterValues.account_id && filterValues.account_id !== 'null') {
      body.accounts = [Number(filterValues.account_id)];
    }

    if (filterValues.campaign_id) {
      body.filters['campaign_id'] = [Number(filterValues.campaign_id)];
    }

    if (filterValues.product_id) {
      body.filters['product_id'] = filterValues.product_id;
    }

    if (filterValues.exclude_abandons) {
      body.fields.push('exclude_abandons');
    }

    if (filterValues.exclude_prefulfillment_cancels) {
      body.fields.push('exclude_prefulfillment_cancels');
    }

    if (group) {
      body.group = group;
    }

    return body;
  }

  getProductFilterOptions(dashboardRoute: string, reportType: number) {
    const filterValues = this.getFilter();
    const body = {
      date_range: {
        startDate: this.convertDateToUtc(filterValues.start_date, filterValues.timezone),
        endDate: this.convertDateToUtc(filterValues.end_date, filterValues.timezone),
      },
      timezone: filterValues.timezone,
      accounts: [],
      report_type: reportType,
      report_version: ReportVersion.Clickhouse,
      grouped_fields: ['product_id', 'product_name'],
    };
    return this.http.post(`${dashboardRoute}/filter_options/`, body);
  }

  getStageFilterOptions(dashboardRoute: string, reportType: number) {
    const filterValues = this.getFilter();
    const body = {
      date_range: {
        startDate: this.convertDateToUtc(filterValues.start_date, filterValues.timezone),
        endDate: this.convertDateToUtc(filterValues.end_date, filterValues.timezone),
      },
      timezone: filterValues.timezone,
      accounts: [],
      report_type: reportType,
      report_version: ReportVersion.Clickhouse,
      field: 'offer_intent',
    };
    return this.http.post(`${dashboardRoute}/filter_options/`, body);
  }

  getPostData() {
    this.http.post(this.route + '/', this.getBody()).subscribe(
      (data) => {
        this.data.next(data);
      },
      (error) => {
        this.data.next({});
        if (error.status !== 401) {
          this.alertService.error(error.message);
        }
      }
    );
  }

  getV2Data(reportType: number, group?: string) {
    this.http.post(this.route + '/', this.getV2Body(reportType, group)).subscribe(
      (data) => {
        if (data) {
          let processed = data;
          // cap results to 5 so charts and tables don't exceed their display limit
          if (data['results'] && Array.isArray(data['results'])) {
            processed = { ...data, results: data['results'].slice(0, 5) };
          }
          this.data.next(processed);
        }
      },
      (error) => {
        this.data.next({});
        if (error.status !== 401) {
          this.alertService.error(error.message);
        }
      }
    );
  }

  getData() {
    if (this.reportType !== null) {
      this.getV2Data(this.reportType, this.group);
    } else if (this.route) {
      this.http
        .get(this.route + "/", { params: this.getQueryParams() })
        .subscribe(
          (data) => {
            if (data) {
              this.data.next(data);
            }
          },
          (error) => {
            if (error.status !== 401) {
              this.data.next({});
              this.alertService.error(error.message);
            }
          }
        );
    }
  }

  get data$() {
    return this.data.asObservable();
  }

  get filters$() {
    return this.filter.asObservable();
  }

  setFilter(data) {
    if ("start_date" in data) {
      data["start_date"] = moment(data["start_date"])
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss");
    }

    if ("end_date" in data) {
      data["end_date"] = moment(data["end_date"])
        .endOf("day")
        .format("YYYY-MM-DD HH:mm:ss");
    }

    this.filter.next(data);
  }

  getFilter(): DashboardFilter {
    return this.filter.getValue();
  }

  protected getTimezone(): string {
    let timezone = this.getTimezoneFromStorage();
    if (!timezone) {
      timezone = moment.tz.guess();
    }
    return timezone;
  }

  protected getAccountIds(): number[] {
    const user = this.storageService.get('currentUser');
    let ids: number[] = [];
    if (user && user.account && user.account.id) {
      ids = [Number(user.account.id)];
    }
    return ids;
  }
}
